import { Vulnerability } from '../../../components/shared'
import { CvssScoringType } from './ui-interfaces'

export const shouldUseCvss3 = (
  vulnerability: Vulnerability,
  cvssScoringType?: CvssScoringType
): boolean => {
  return cvssScoringType === CvssScoringType.CVSS3_1 && vulnerability.hasCvss3
    ? true
    : false
}

export interface CvssVector {
  key: string
  value: string
}
export interface Cvss3Vectors {
  attackVector?: CvssVector
  attackComplexity?: CvssVector
  privilegesRequired?: CvssVector
  confidentialityImpact?: CvssVector
  integrityImpact?: CvssVector
  availabilityImpact?: CvssVector
  scope?: CvssVector
  userInteraction?: CvssVector
}

export interface Cvss2Vectors {
  visible?: CvssVector
  accessVector?: CvssVector
  accessComplexity?: CvssVector
  availabilityImpact?: CvssVector
  confidentialityImpact?: CvssVector
  integrityImpact?: CvssVector
  authentication?: CvssVector
}

const vectorToObj = (
  key: string,
  cvss3Vectors: Cvss3Vectors,
  value: string,
  objKey: string
) => {
  return {
    ...cvss3Vectors,
    [objKey]: {
      key,
      value
    }
  }
}

export const mapCVSS2Vector = (
  cvssVectors: Cvss2Vectors,
  vectorKey: string
) => {
  const vectors = {
    ['AV:N']: { value: 'Network', key: 'accessVector' },
    ['AV:A']: { value: 'Adjacent Network', key: 'accessVector' },
    ['AV:L']: { value: 'Local', key: 'accessVector' },
    ['AC:L']: { value: 'Low', key: 'accessComplexity' },
    ['AC:M']: { value: 'Medium', key: 'accessComplexity' },
    ['AC:H']: { value: 'High', key: 'accessComplexity' },
    ['Au:M']: { value: 'Multiple', key: 'authentication' },
    ['Au:N']: { value: 'None', key: 'authentication' },
    ['C:N']: { value: 'None', key: 'confidentialityImpact' },
    ['C:P']: { value: 'Partial', key: 'confidentialityImpact' },
    ['C:C']: { value: 'Complete', key: 'confidentialityImpact' },
    ['I:N']: { value: 'None', key: 'integrityImpact' },
    ['I:P']: { value: 'Partial', key: 'integrityImpact' },
    ['I:C']: { value: 'Complete', key: 'integrityImpact' },
    ['A:N']: { value: 'None', key: 'availabilityImpact' },
    ['A:P']: { value: 'Partial', key: 'availabilityImpact' },
    ['A:C']: { value: 'Complete', key: 'availabilityImpact' }
  }

  if (vectors[vectorKey]) {
    cvssVectors = vectorToObj(
      vectorKey,
      cvssVectors,
      vectors[vectorKey].value,
      vectors[vectorKey].key
    )
  }

  return cvssVectors
}

export const mapCVSS3Vector = (
  cvssVectors: Cvss3Vectors,
  vectorKey: string
) => {
  switch (vectorKey) {
    case 'AV:N':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Network',
        'attackVector'
      )
      break
    case 'AV:A':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Adjacent Network',
        'attackVector'
      )
      break
    case 'AV:L':
      cvssVectors = vectorToObj(vectorKey, cvssVectors, 'Local', 'attackVector')
      break
    case 'AV:P':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Physical',
        'attackVector'
      )
      break
    case 'AC:L':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Low',
        'attackComplexity'
      )
      break
    case 'AC:H':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'High',
        'attackComplexity'
      )
      break
    case 'PR:N':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'None',
        'privilegesRequired'
      )
      break
    case 'PR:L':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Low',
        'privilegesRequired'
      )
      break
    case 'PR:H':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'High',
        'privilegesRequired'
      )
      break
    case 'C:N':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'None',
        'confidentialityImpact'
      )
      break
    case 'C:L':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Low',
        'confidentialityImpact'
      )
      break
    case 'C:H':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'High',
        'confidentialityImpact'
      )
      break
    case 'I:N':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'None',
        'integrityImpact'
      )
      break
    case 'I:L':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Low',
        'integrityImpact'
      )
      break
    case 'I:H':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'High',
        'integrityImpact'
      )
      break
    case 'A:N':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'None',
        'availabilityImpact'
      )
      break
    case 'A:L':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Low',
        'availabilityImpact'
      )
      break
    case 'A:H':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'High',
        'availabilityImpact'
      )
      break
    case 'UI:N':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'None',
        'userInteraction'
      )
      break
    case 'UI:R':
      cvssVectors = vectorToObj(
        vectorKey,
        cvssVectors,
        'Required',
        'userInteraction'
      )
      break
    case 'S:U':
      cvssVectors = vectorToObj(vectorKey, cvssVectors, 'Unchanged', 'scope')
      break
    case 'S:C':
      cvssVectors = vectorToObj(vectorKey, cvssVectors, 'Changed', 'scope')
      break
    default:
      break
  }

  return cvssVectors
}

export const removeParam = (sourceURL: string, key: string) => {
  let rtn = sourceURL.split('?')[0],
    param,
    paramsArr: string[] = []
  const queryString =
    sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : ''
  if (queryString !== '') {
    paramsArr = queryString.split('&')
    for (let i = paramsArr.length - 1; i >= 0; i -= 1) {
      param = paramsArr[i].split('=')[0]
      if (param === key) {
        paramsArr.splice(i, 1)
      }
    }
    if (paramsArr.length) {
      rtn = rtn + '?' + paramsArr.join('&')
    }
  }
  return rtn
}

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

export const clearPanelParameters = (url: string) => {
  let newUrl = removeParam(url, 'activeRow')
  newUrl = removeParam(newUrl, 'showPanel')

  return newUrl
}

export const parseCvssVectors = (
  vector: string,
  cvssScoringType: CvssScoringType
): Cvss3Vectors | Cvss2Vectors | undefined => {
  if (!vector) {
    return
  }
  const vectors = vector.split('/')

  if (cvssScoringType === CvssScoringType.CVSS3_1) {
    let cvss3Vectors: Cvss3Vectors = {}

    for (const vectorKey of vectors) {
      cvss3Vectors = mapCVSS3Vector(cvss3Vectors, vectorKey)
    }

    return cvss3Vectors
  }

  if (cvssScoringType === CvssScoringType.CVSS2) {
    let cvss2Vectors: Cvss2Vectors = {}

    for (const vectorKey of vectors) {
      cvss2Vectors = mapCVSS2Vector(cvss2Vectors, vectorKey)
    }

    return cvss2Vectors
  }
}
